import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import apiRequest from './apiRequest';
import { tokenConfig } from './tokenConfig';
import { getBaseUrl } from './baseUrlService';

interface ApiError {
  response?: {
    data?: {
      error?: {
        message?: string;
      };
    };
    status?: number;
  };
  message?: string;
}

const BASE_URL = getBaseUrl();

const handleError = (err: unknown): never => {
  const error = err as AxiosError;
  const serverErrorMessage = (error as ApiError)?.response?.data?.error?.message;
  Sentry.captureException(error);

  if (error.response) {
    // Attach a human-readable message but keep the original error for status detection
    const status = error.response.status;
    let message = serverErrorMessage || '';

    switch (status) {
      case 400:
        message = message || 'Bad Request. Please check your input.';
        break;
      case 401:
        console.log('Redirect to login');
        break;
      case 403:
        message = message || 'Forbidden. You do not have permission to access this resource.';
        break;
      case 404:
        message = message || 'Resource not found.';
        break;
      case 500:
        message = message || 'Internal Server Error. Please try again later.';
        break;
      default:
        message = message || `Unexpected error occurred: ${status}`;
        break;
    }

    // Throw the original error so retry logic can read response.status
    error.message = message;
    throw error;
  }

  if (error.message) {
    if (error.code === 'ERR_NETWORK') {
      error.message = 'Network error. Please check your internet connection.';
    }
    throw error;
  }

  throw new Error('An unknown error occurred.');
};

export const axiosGetRequest = async (api: string, params = {}) => {
  try {
    const { data } = await apiRequest.get(`${BASE_URL}/${api}`, {
      params,
      ...tokenConfig(),
    });

    return data;
  } catch (err: unknown) {
    handleError(err);
  }
};

export const axiosPostRequest = async (api: string, params = {}) => {
  try {
    const { data } = await apiRequest.post(`${BASE_URL}/${api}`, params, {
      ...tokenConfig(),
    });

    return data;
  } catch (err: unknown) {
    handleError(err);
  }
};

export const axiosPutRequest = async (api: string, params = {}) => {
  try {
    const { data } = await apiRequest.put(`${BASE_URL}/${api}`, params, {
      ...tokenConfig(),
    });

    return data;
  } catch (err: unknown) {
    handleError(err);
  }
};

export const axiosDeleteRequest = async (
  api: string,
  queryParams: Record<string, string | number> = {} // Object for dynamic queries
) => {
  try {
    const queryString = new URLSearchParams(queryParams as Record<string, string>).toString();

    const url = `${BASE_URL}/${api}${queryString ? `?${queryString}` : ''}`;

    const { data } = await apiRequest.delete(url, {
      ...tokenConfig(),
    });

    return data;
  } catch (err: unknown) {
    handleError(err);
  }
};

export const axiosPatchRequest = async (
  api: string,
  params = {},
  queryParams: Record<string, string | number> = {} // Object for dynamic queries
) => {
  try {
    const queryString = new URLSearchParams(queryParams as Record<string, string>).toString();

    const url = `${BASE_URL}/${api}${queryString ? `?${queryString}` : ''}`;

    const { data } = await apiRequest.patch(url, params, {
      ...tokenConfig(),
    });

    return data;
  } catch (err: unknown) {
    handleError(err);
  }
};

// // Example utility to handle different request types dynamically
// export const axiosRequest = async (method: string, api: string, params = {}) => {
//   try {
//     const config = {
//       method,
//       url: `${BASE_URL}/${api}`,
//       ...(method === "GET" ? { params } : { data: params }),
//     };

//     const { data } = await apiRequest(config);
//     return data;
//   } catch (err: unknown) {
//     handleError(err);
//   }
// };

// Exporting all methods
export default {
  get: axiosGetRequest,
  post: axiosPostRequest,
  put: axiosPutRequest,
  delete: axiosDeleteRequest,
  patch: axiosPatchRequest,
  // request: axiosRequest,
};
